/**
 * MfaSetupWizard — canonical 3-step MFA enrollment wizard.
 *
 * Standards-ID: MFA-FACTORY-001 (GEN0-SWEEP-001 Wave 2 Part B)
 *
 * Promoted from `apps/planner-web/src/features/auth/components/MfaSetupWizard.jsx`
 * with `appName` brand prop added so non-planner consumers can reuse it.
 *
 * Step machine:
 *   1. configure — a scannable QR code of the otpauth URI (`OtpauthQrCode`), the manual
 *      secret key, and the URI as a link for a reader on the same device.
 *   2. verify — Enter 6-digit TOTP code to confirm setup.
 *   3. backup — Display + acknowledge backup codes.
 *
 * The wizard is purely view-layer: all async lifecycle (begin / confirm /
 * complete) lives in the caller via the `step` prop + `onVerify` /
 * `onComplete` / `onCancel` callbacks. The wizard owns ONLY local UI state
 * (code input + acknowledgement checkbox).
 *
 * Per CSS-001: uses canonical utility classes from `@puffin-core/shared-styles`
 * (`.mfa-setup-wizard`, `.mfa-setup-steps`, `.form-group`, `.btn`, etc.).
 * No inline styles, no BEM, no co-located CSS.
 *
 * @module @puffin-core/mfa-ui/MfaSetupWizard
 */

import { useState } from 'react';

import { useMfaTranslate } from './hooks/useMfaTranslate.js';
import { OtpauthQrCode } from './OtpauthQrCode.jsx';
import { MfaCodeInput } from './MfaCodeInput.jsx';
import { MfaBackupStep } from './internal/MfaBackupStep.jsx';
import PropTypes from 'prop-types';

const STEP_INDEX = { configure: 0, verify: 1, backup: 2 };

// The progress segments' accessible names — catalog keys, so a Spanish screen reader hears Spanish.
const STEP_LABEL_KEYS = ['setup_step_configure', 'setup_step_verify', 'setup_step_backup'];

/**
 * Step 1 — the QR code of the otpauth URI to scan, the manual key, and the URI as a link for a phone
 * reading this page (F-98-23). Module-private.
 */
function MfaConfigureStep({ brandLabel, secret, qrUri, loading, onCancel, onNext, t }) {
  const str = useMfaTranslate(t);

  return (
    <div>
      <h3 className="mfa-challenge-title">{str('setup_title_configure')}</h3>
      <p className="mfa-challenge-subtitle">
        {str('setup_configure_intro', { brand: brandLabel })}
      </p>

      {qrUri && (
        <div className="mfa-setup-section">
          <OtpauthQrCode uri={qrUri} label={str('setup_qr_label', { brand: brandLabel })} />
        </div>
      )}

      <div className="mfa-setup-section">
        <div className="mfa-setup-section-label">{str('setup_manual_key')}</div>
        <div className="mfa-setup-secret" data-testid="mfa-secret">
          {secret}
        </div>
      </div>

      {qrUri && (
        <div className="mfa-setup-section">
          <div className="mfa-setup-section-label">{str('setup_uri_label')}</div>
          <a className="mfa-setup-uri" href={qrUri} data-testid="mfa-qr-uri">
            {qrUri}
          </a>
        </div>
      )}

      <div className="mfa-setup-actions">
        <button type="button" className="btn btn-secondary" onClick={onCancel} disabled={loading}>
          {str('setup_btn_cancel')}
        </button>
        <button type="button" className="btn btn-primary" onClick={onNext} disabled={loading}>
          {str('setup_btn_next')}
        </button>
      </div>
    </div>
  );
}

MfaConfigureStep.propTypes = {
  brandLabel: PropTypes.string.isRequired,
  secret: PropTypes.string,
  qrUri: PropTypes.string,
  loading: PropTypes.bool.isRequired,
  onCancel: PropTypes.func.isRequired,
  onNext: PropTypes.func.isRequired,
};

/**
 * Step 2 — enter the 6-digit TOTP to confirm setup. Module-private.
 *
 * The code is the ONE code field (D12b): it takes focus when the step opens and never submits on
 * the sixth digit (the person confirms the enrolment with Verify, or Enter).
 */
function MfaVerifyStep({ code, onCodeChange, loading, onSubmit, onCancel, t }) {
  // A module-private step component, but still a component — it renders user-facing copy, so it
  // resolves it the same way every other surface in this package does.
  const str = useMfaTranslate(t);

  return (
    <div>
      <h3 className="mfa-challenge-title">{str('setup_title_verify')}</h3>
      <p className="mfa-challenge-subtitle">{str('setup_verify_intro')}</p>

      <form onSubmit={onSubmit}>
        <MfaCodeInput
          id="mfa-verify-code"
          code={code}
          onChange={onCodeChange}
          label={str('setup_code_label')}
          autoSubmit={false}
          focusOnMount
          isLoading={loading}
          placeholder="000000"
        />

        <div className="mfa-setup-actions">
          <button type="button" className="btn btn-secondary" onClick={onCancel} disabled={loading}>
            {str('setup_btn_cancel')}
          </button>
          <button type="submit" className="btn btn-primary" disabled={code.length === 0 || loading}>
            {str(loading ? 'setup_btn_verifying' : 'setup_btn_verify')}
          </button>
        </div>
      </form>
    </div>
  );
}

MfaVerifyStep.propTypes = {
  code: PropTypes.string.isRequired,
  onCodeChange: PropTypes.func.isRequired,
  loading: PropTypes.bool.isRequired,
  onSubmit: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
};

/**
 * Step-by-step wizard for configuring MFA (TOTP setup, code verification,
 * backup codes).
 *
 * @param {Object} props
 * @param {'configure'|'verify'|'backup'} props.step - Current wizard step
 * @param {boolean} props.loading - Whether an async operation is in progress
 * @param {string|null} [props.error] - Error message to display
 * @param {string} [props.secret] - TOTP secret key (configure step)
 * @param {string} [props.qrUri] - QR code URI for authenticator app (configure step)
 * @param {string[]} [props.backupCodes] - Generated backup codes (backup step)
 * @param {string} [props.appName] - Brand name shown in the configure-step body copy
 * @param {Function} props.onVerify - Verify-step callback `(code: string) => void`. On configure step, called with no args to advance.
 * @param {Function} props.onComplete - Backup-step "Complete Setup" callback
 * @param {Function} props.onCancel - Cancel callback (any step)
 * @returns {React.JSX.Element}
 */
export function MfaSetupWizard({
  step,
  loading,
  error,
  secret,
  qrUri,
  backupCodes,
  appName,
  onVerify,
  onComplete,
  onCancel,
  t,
}) {
  const str = useMfaTranslate(t);

  const [code, setCode] = useState('');
  const [acknowledged, setAcknowledged] = useState(false);

  const handleVerifySubmit = (e) => {
    e.preventDefault();
    if (code.length > 0 && !loading) {
      onVerify(code);
      setCode('');
    }
  };

  const currentStepIndex = STEP_INDEX[step] ?? -1;
  const brandLabel = appName || 'your account';

  // Pre-resolved slots: the error banner + the three mutually-exclusive step
  // bodies. Every `&&` is preserved VERBATIM — no `!!` coercion, no early
  // return — so render semantics are byte-for-byte what they were.
  const errorBanner = error && (
    <div className="form-error" role="alert">
      {error}
    </div>
  );

  const configureStep = step === 'configure' && (
    <MfaConfigureStep
      t={t}
      brandLabel={brandLabel}
      secret={secret}
      qrUri={qrUri}
      loading={loading}
      onCancel={onCancel}
      onNext={onVerify}
    />
  );

  const verifyStep = step === 'verify' && (
    <MfaVerifyStep
      code={code}
      onCodeChange={setCode}
      loading={loading}
      onSubmit={handleVerifySubmit}
      onCancel={onCancel}
      t={t}
    />
  );

  const backupStep = step === 'backup' && (
    <MfaBackupStep
      t={t}
      backupCodes={backupCodes}
      acknowledged={acknowledged}
      onAcknowledgeChange={(e) => setAcknowledged(e.target.checked)}
      onComplete={onComplete}
    />
  );

  return (
    <div className="mfa-setup-wizard" role="region" aria-label={str('setup_region_label')}>
      <div
        className="mfa-setup-steps"
        role="progressbar"
        aria-valuenow={currentStepIndex + 1}
        aria-valuemin={1}
        aria-valuemax={3}
      >
        {STEP_LABEL_KEYS.map((labelKey, idx) => {
          let className = 'mfa-setup-step';
          if (idx < currentStepIndex) className += ' mfa-setup-step-complete';
          else if (idx === currentStepIndex) className += ' mfa-setup-step-active';
          return <div key={labelKey} className={className} aria-label={str(labelKey)} />;
        })}
      </div>

      {errorBanner}

      {configureStep}

      {verifyStep}

      {backupStep}
    </div>
  );
}

MfaSetupWizard.propTypes = {
  t: PropTypes.func,
  step: PropTypes.oneOf(['configure', 'verify', 'backup']).isRequired,
  loading: PropTypes.bool.isRequired,
  error: PropTypes.string,
  secret: PropTypes.string,
  qrUri: PropTypes.string,
  backupCodes: PropTypes.arrayOf(PropTypes.string),
  appName: PropTypes.string,
  onVerify: PropTypes.func.isRequired,
  onComplete: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
};

export default MfaSetupWizard;
